import {Price, Values} from '@wix/devzai-utils-common';
import {doppeHideableValueGetVisibleValue} from './doppe-hideable-value';
import {RichTextContent} from '@wix/devzai-utils-react';
import {DoppeCheckoutSettings} from './doppe-checkout-action-settings';
import {DoppeActionTypeRequest} from '../../doppe-action-types/doppe-action-type-request/doppe-action-type-request';
import {doppeActionPriceConvertActionPriceToPrice} from './doppe-action-price';
import {
    DoppeActionTypePaidMessage,
} from '../../doppe-action-types/doppe-action-type-paid-message/doppe-action-type-paid-message';
import {DoppeActionSettingsCTAButtonSettings} from './doppe-action-cta-button';

export const DoppeDtoCheckoutIntentType = {
    Request: 'Request',
    PaidDM: 'PaidDM',
    Donate: 'Donate',
    Custom: 'Custom',
} as const;

export type DoppeDtoCheckoutType = Values<typeof DoppeDtoCheckoutIntentType>;

export type DoppeDtoCheckoutIntent =
    | DoppeDtoCheckoutIntent.Request
    | DoppeDtoCheckoutIntent.PaidDM
    | DoppeDtoCheckoutIntent.Donate
    | DoppeDtoCheckoutIntent.Custom;

type DoppeDtoCheckoutIntentServerTypes = {
    uouCheckoutData: DoppeDtoCheckoutIntent.UouCheckoutData;
    actionMetadata: DoppeDtoCheckoutIntent.DoppeActionMetadata;
};

export type DoppeDtoCheckoutIntentServer = DoppeDtoCheckoutIntent & DoppeDtoCheckoutIntentServerTypes;

export namespace DoppeDtoCheckoutIntentServer {
    export type Donate = DoppeDtoCheckoutIntent.Donate & DoppeDtoCheckoutIntentServerTypes;
    export type PaidDM = DoppeDtoCheckoutIntent.PaidDM & DoppeDtoCheckoutIntentServerTypes;
    export type Request = DoppeDtoCheckoutIntent.Request & DoppeDtoCheckoutIntentServerTypes;
    export type Custom = DoppeDtoCheckoutIntent.Custom & DoppeDtoCheckoutIntentServerTypes;
}

export namespace DoppeDtoCheckoutIntent {
    export type UouCheckoutData = {
        uouName: string;
        uouEmail: string;
        uouPhone?: string;
        uouAddress?: string;
        uouEmailSubscription?: boolean;
    };

    export type DoppeActionMetadata = {
        actionId: string;
        actionName: string;
        pageId: string;
        linkId: string;
        innerItemId?: string;
    };

    type BaseType<T extends DoppeDtoCheckoutType> = {
        price: Price;
        checkoutSettings: DoppeCheckoutSettings;
        uouCheckoutData?: UouCheckoutData;
        type: T;
    };

    export type Request = BaseType<typeof DoppeDtoCheckoutIntentType.Request> & {
        name: string;
        requestTitle: string;
        description?: RichTextContent;
        instructions?: RichTextContent;
        fulfillmentDetails?: RichTextContent;
        note?: string;
    };

    export type PaidDM = BaseType<typeof DoppeDtoCheckoutIntentType.PaidDM> & {
        name: string;
        description?: RichTextContent;
        message: string;
    };

    export type Donate = BaseType<typeof DoppeDtoCheckoutIntentType.Donate> & {
        name?: string;
        note?: string;
    };

    export type Custom = BaseType<typeof DoppeDtoCheckoutIntentType.Custom> & {
        name?: string;
        description?: RichTextContent;
        fulfillmentDetails?: RichTextContent;
    };
}

export function doppeDtoCheckoutIntentCreateRequest(options: {
    actionName: string;
    actionSettings: DoppeActionTypeRequest.RequestOption;
    checkoutSettings: DoppeCheckoutSettings;
    note?: string;
}) {
    const {
        actionName,
        actionSettings,
        checkoutSettings,
        note,
    } = options;

    return {
        type: DoppeDtoCheckoutIntentType.Request,
        price: doppeActionPriceConvertActionPriceToPrice(actionSettings.priceData),
        checkoutSettings: checkoutSettings,
        fulfillmentDetails: doppeHideableValueGetVisibleValue(actionSettings.fulfillmentDetails, undefined),
        instructions: doppeHideableValueGetVisibleValue(actionSettings.requestsInstructions, undefined),
        name: actionName,
        requestTitle: actionSettings.title,
        description: doppeHideableValueGetVisibleValue(actionSettings.description, undefined),
        note: note,
    } satisfies DoppeDtoCheckoutIntent.Request;
}

export function doppeDtoCheckoutIntentCreatePaidDM(options: {
    actionSettings: DoppeActionTypePaidMessage.ActionSettings;
    checkoutSettings: DoppeCheckoutSettings;
    message: string;
}) {
    const {
        actionSettings,
        checkoutSettings,
        message,
    } = options;

    return {
        type: DoppeDtoCheckoutIntentType.PaidDM,
        price: doppeActionPriceConvertActionPriceToPrice(actionSettings.priceData),
        checkoutSettings: checkoutSettings,
        name: actionSettings.title,
        description: doppeHideableValueGetVisibleValue(actionSettings.description, undefined),
        message: message,
    } satisfies DoppeDtoCheckoutIntent.PaidDM;
}

export function doppeDtoCheckoutIntentCreateDonate(options: {
    amount: number;
    checkoutSettings: DoppeCheckoutSettings;
    note?: string;
    name: string;
}) {
    const {
        checkoutSettings,
        amount,
        note,
        name,
    } = options;

    return {
        type: DoppeDtoCheckoutIntentType.Donate,
        price: {
            amount: amount,
            currencyCode: checkoutSettings.currencyCode,
        },
        checkoutSettings: checkoutSettings,
        note: note,
        name: name,
    } satisfies DoppeDtoCheckoutIntent.Donate;
}

export function doppeDtoCheckoutIntentCreateCustomFromCTAButton(options: {
    ctaButtonSettings: DoppeActionSettingsCTAButtonSettings.Checkout;
    checkoutSettings: DoppeCheckoutSettings;
    fulfillmentDetails?: RichTextContent;
}) {
    const {
        ctaButtonSettings,
        checkoutSettings,
        fulfillmentDetails,
    } = options;

    return {
        type: DoppeDtoCheckoutIntentType.Custom,
        price: doppeActionPriceConvertActionPriceToPrice(ctaButtonSettings.price),
        checkoutSettings: checkoutSettings,
        name: doppeHideableValueGetVisibleValue(ctaButtonSettings.name, undefined),
        description: doppeHideableValueGetVisibleValue(ctaButtonSettings.description, undefined),
        fulfillmentDetails: fulfillmentDetails,
    } satisfies DoppeDtoCheckoutIntent.Custom;
}
